import {
  normalizeOrderSide,
  normalizeOrderSymbol,
  parseOrderDate,
} from "@helpers/formatOrders";
import { getPairMeta } from "../../../Quotes/pairMeta";
import { COIN_ICONS } from "../../../Orders/Community/communityMockData";
import { stripQuoteAsset, toNumber } from "../../../Orders/positionFormat";
import { WALLET_TYPE } from "@constants/wallet-type";
import { getMockTradeAmounts, getQuantity } from "./tradeMath";

/**
 * Trades of our positions API and of the community tables in one shape,
 * the one the trade details modal shows:
 *
 * @typedef {object} TradeDetails
 * @property {string} symbol "BTCUSDT"
 * @property {string} coin "BTC"
 * @property {string} [coinName] "Bitcoin"
 * @property {string} [coinIcon]
 * @property {boolean} isLong
 * @property {number|null} entryPrice
 * @property {number|null} exitPrice null while the trade is open
 * @property {number|null} quantity
 * @property {number|null} wager
 * @property {number} feeInPnl the open fee a closed trade's P&L includes
 * @property {number|null} leverage
 * @property {number|null} finalPnl realized P&L of a closed trade, with fees
 * @property {number|null} openedAt ms
 * @property {number|null} closedAt ms, null while the trade is open
 * @property {boolean} isClosed
 * @property {string|null} user
 * @property {string|number|null} tradeId
 * @property {boolean} isDemo
 * @property {object|null} shareOrder own order for the P&L share card
 * @property {string|null} [createdAt] open date as our API sends it
 */

const CLOSED_STATUS = "closed";
const DEMO_WALLET_TYPES = [WALLET_TYPE.DEMO, WALLET_TYPE.DEMO_FUTURES];

/** Trades of the demo account are marked in the modal */
export const isDemoWallet = (wallet) => DEMO_WALLET_TYPES.includes(wallet?.type);

const parseNumber = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const numeric = parseFloat(String(value ?? "").replace(/[^0-9.-]/g, ""));

  return Number.isFinite(numeric) ? numeric : null;
};

const toTime = (value) => parseOrderDate(value)?.getTime() ?? null;

const getCoinInfo = (symbol, fallbackIcon) => {
  const meta = getPairMeta(symbol);

  return {
    symbol,
    coin: stripQuoteAsset(symbol),
    coinName: meta?.fullName ?? null,
    coinIcon: meta?.icon ?? fallbackIcon ?? null,
  };
};

/**
 * Own position, open or closed.
 * @param {object} order position of our API
 * @param {{placedBy: string, isDemo: boolean}} options
 * @returns {TradeDetails}
 */
export const fromPosition = (order, { placedBy, isDemo }) => {
  const { isSellSide } = normalizeOrderSide(order?.side);
  const closedAt = toTime(order?.closed_at ?? order?.close_time);
  const isClosed =
    closedAt != null ||
    String(order?.status ?? "").toLowerCase() === CLOSED_STATUS;
  const entryPrice = toNumber(
    order?.entry_price ?? order?.fill_price ?? order?.price,
  );
  const wager = toNumber(order?.margin);
  const leverage = toNumber(order?.leverage);
  const quantity = Math.abs(toNumber(order?.quantity ?? order?.qty) ?? 0);

  return {
    ...getCoinInfo(normalizeOrderSymbol(order?.symbol)),
    isLong: !isSellSide,
    entryPrice,
    exitPrice: isClosed
      ? toNumber(order?.closed_price ?? order?.close_price)
      : null,
    quantity: quantity || getQuantity(wager, leverage, entryPrice),
    wager,
    feeInPnl: isClosed ? toNumber(order?.open_fee) ?? 0 : 0,
    leverage,
    finalPnl: isClosed ? toNumber(order?.realized_pnl ?? order?.pnl) : null,
    openedAt: toTime(order?.created_at),
    closedAt,
    isClosed,
    user: placedBy,
    tradeId: order?.id ?? null,
    isDemo,
    shareOrder: order,
  };
};

/**
 * Row of the public feed, best trades or an open trade of a trader profile.
 * @param {object} row
 * @returns {TradeDetails}
 */
export const fromCommunityTrade = (row) => {
  const coin = row.trade?.coin ?? row.trade?.symbol ?? "";
  const isClosed = !row.isOpen;
  const entryPrice = row.entryPrice ?? parseNumber(row.entry);
  const shownWager = parseNumber(row.wager);
  const leverage = parseNumber(row.leverage);
  // An open mock trade already shows what is left of the wager after the fee
  const { margin, wager, feeInPnl } = isClosed
    ? getMockTradeAmounts(shownWager, false)
    : { margin: shownWager, wager: shownWager, feeInPnl: 0 };
  const fallbackIcon =
    COIN_ICONS[row.trade?.coinIcon ?? row.trade?.assetIcon] ?? COIN_ICONS[coin];

  return {
    ...getCoinInfo(`${coin}USDT`, fallbackIcon),
    isLong: row.trade?.direction !== "down",
    entryPrice,
    exitPrice: isClosed ? row.exitPrice ?? parseNumber(row.exit) : null,
    quantity: getQuantity(margin, leverage, entryPrice),
    wager,
    feeInPnl,
    leverage,
    finalPnl: isClosed ? parseNumber(row.profitLoss ?? row.profit) : null,
    openedAt: row.openedAt ?? toTime(row.created_at),
    closedAt: row.closedAt ?? null,
    isClosed,
    user: row.username ?? row.user ?? null,
    tradeId: row.tradeId ?? null,
    isDemo: false,
    shareOrder: null,
    // created_at as our API sends it, for the share card
    createdAt: row.created_at ?? null,
  };
};

/**
 * Order for the P&L share card: the own one, or built from the details
 * for someone else's trade.
 * @param {TradeDetails} trade
 * @param {number|null} price price the card shows the trade at
 */
export const toShareOrder = (trade, price) =>
  trade.shareOrder ?? {
    symbol: trade.symbol,
    side: trade.isLong ? "long" : "short",
    leverage: trade.leverage,
    entry_price: trade.entryPrice,
    closed_price: price,
    status: trade.isClosed ? "Closed" : "Open",
    created_at: trade.createdAt,
  };
